"use client";

import { useMemo } from "react";
import dynamic from "next/dynamic";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { EChartsOption } from "echarts";

const ReactECharts = dynamic(() => import("echarts-for-react"), { ssr: false });

type ProvenanceRow = {
  DOCID: string;
  continent: string;
  Source_Location_or_Regional_Focus: string;
  Source_General_Institution_Category: string;
  general_group: string;
  Source_Particular_Institution_or_Specialization: string;
  CreditorGeographicRegion: string;
  BorrowerGeographicRegion: string;
  pCreditorType: string;
  inferred_denominations_readable: string;
  Country: string;
  DocumentYear: number;
  source: string;
  source_cat: string;
  target: string;
  target_cat: string;
  doc_count?: number;
};

type ProvenanceSankeyProps = {
  rows: ProvenanceRow[];
  noCard?: boolean;
};

export default function ProvenanceSankey({
  rows,
  noCard = false,
}: ProvenanceSankeyProps) {
  const isMobile = useMediaQuery("(max-width:900px)");

  const sankeyOption = useMemo(() => {
    const CATEGORY_DEPTH: Record<string, number> = {
      continent: 0,
      Source_Location_or_Regional_Focus: 1,
      Source_General_Institution_Category: 2,
      general_group: 3,
      Source_Particular_Institution_or_Specialization: 4,
    };

    const stripLevelPrefix = (name: string) => name.replace(/^\[\d\]\s/, "");

    const getNodeDepth = (name: string) => {
      const match = name.match(/^\[(\d)\]\s/);
      return match ? Number(match[1]) : -1;
    };

    const formatRightColumnLabel = (name: string) => {
      let normalized = stripLevelPrefix(name);

      if (normalized.startsWith("Central Government - ")) {
        normalized = normalized.replace("Central Government - ", "");
      } else if (normalized.startsWith("General Government - ")) {
        normalized = normalized.replace("General Government - ", "");
      } else if (normalized.startsWith("MDB - ")) {
        normalized = normalized.replace("MDB - ", "");
      } else if (normalized.startsWith("Regulatory - ")) {
        normalized = normalized.replace("Regulatory - ", "");
      } else if (normalized.startsWith("Media - ")) {
        normalized = normalized.replace("Media - ", "");
      }

      return normalized;
    };

    const formatLabelName = (name: string) => {
      const nodeDepth = getNodeDepth(name);

      if (nodeDepth === 4) {
        return formatRightColumnLabel(name);
      }

      const strippedName = stripLevelPrefix(name);

      return strippedName.length > 28
        ? `${strippedName.substring(0, 25)}...`
        : strippedName;
    };

    // Use level-prefixed node names to ensure uniqueness and stable column layout.
    const nodes: Array<{ name: string }> = [];
    const nodeSet = new Set<string>();

    // Build Sankey data with level prefixed node names
    const links: Array<{
      source: string;
      target: string;
      value: number;
    }> = [];

    const linkMap = new Map<string, number>();

    rows.forEach((row) => {
      const sourceDepth = CATEGORY_DEPTH[row.source_cat];
      const targetDepth = CATEGORY_DEPTH[row.target_cat];
      if (
        sourceDepth === undefined ||
        targetDepth === undefined ||
        !row.source ||
        !row.target
      ) {
        return;
      }

      const sourceNode = `[${sourceDepth}] ${row.source}`;
      const targetNode = `[${targetDepth}] ${row.target}`;
      nodeSet.add(sourceNode);
      nodeSet.add(targetNode);

      const linkKey = `${sourceNode}|${targetNode}`;
      const linkValue =
        typeof row.doc_count === "number" && Number.isFinite(row.doc_count)
          ? row.doc_count
          : 0;

      // Rows are repeated by DOCID; keep the precomputed edge count (doc_count)
      // rather than summing duplicates.
      const existingValue = linkMap.get(linkKey);
      if (existingValue === undefined || linkValue > existingValue) {
        linkMap.set(linkKey, linkValue);
      }
    });

    // Convert link map to ECharts format
    linkMap.forEach((count, link) => {
      const [source, target] = link.split("|");
      links.push({
        source,
        target,
        value: count,
      });
    });

    const incomingByNode = new Map<string, number>();
    const outgoingByNode = new Map<string, number>();

    links.forEach(({ source, target, value }) => {
      outgoingByNode.set(source, (outgoingByNode.get(source) ?? 0) + value);
      incomingByNode.set(target, (incomingByNode.get(target) ?? 0) + value);
    });

    const nodeDocumentCounts = new Map<string, number>();
    nodeSet.forEach((nodeName) => {
      const depth = getNodeDepth(nodeName);
      const incoming = incomingByNode.get(nodeName) ?? 0;
      const outgoing = outgoingByNode.get(nodeName) ?? 0;

      // Left-most nodes have only outgoing links; all other levels use incoming.
      const documents = depth === 0 ? outgoing : incoming || outgoing;
      nodeDocumentCounts.set(nodeName, documents);
    });

    // Create nodes with formatted display names
    nodeSet.forEach((nodeName) => {
      nodes.push({ name: nodeName });
    });

    return {
      tooltip: {
        trigger: "item",
        formatter: (params: unknown) => {
          const point = Array.isArray(params) ? params[0] : params;
          const typedPoint = point as {
            dataType?: string;
            name?: string;
            data?: { name?: string };
            value?: number;
            source?: string;
            target?: string;
          };

          if (typedPoint.dataType === "node") {
            const rawNodeName = typedPoint.name ?? typedPoint.data?.name ?? "";
            const displayName =
              getNodeDepth(rawNodeName) === 4
                ? formatRightColumnLabel(rawNodeName)
                : stripLevelPrefix(rawNodeName);
            const documents = nodeDocumentCounts.get(rawNodeName) ?? 0;
            return `${displayName}<br/>Documents: ${documents}`;
          }

          const typedValue = typedPoint as {
            value?: number;
            source?: string;
            target?: string;
          };
          const { value, source, target } = typedValue;
          if (source && target) {
            const sourceName = source.replace(/^\[\d\]\s/, "");
            const targetName = target.replace(/^\[\d\]\s/, "");
            return `${sourceName} → ${targetName}<br/>Documents: ${value}`;
          }
          return `Documents: ${value}`;
        },
      },
      series: [
        {
          type: "sankey",
          left: "2%",
          right: 240,
          top: "4%",
          bottom: "4%",
          data: nodes,
          links: links,
          nodeWidth: 12,
          nodeGap: 12,
          lineStyle: {
            color: "source",
            opacity: 0.3,
            curveness: 0.5,
          },
          label: {
            color: "#2f3e4e",
            fontSize: 11,
            fontFamily: "Inter",
            fontWeight: 500,
            formatter: ({ name }: { name: string }) => formatLabelName(name),
            textBorderColor: "#ffffff",
            textBorderWidth: 1,
          },
          labelLayout: { moveOverlap: "shiftY" },
          emphasis: {
            focus: "trajectory",
          },
        },
      ],
    } as EChartsOption;
  }, [rows]);

  const inner = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#363636" }}>
          Document Source Provenance
        </Typography>
      </Box>
      <Divider sx={{ mb: 4 }} />
      <Box
        sx={{
          width: "100%",
          overflowX: isMobile ? "auto" : "visible",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Box sx={{ width: isMobile ? 980 : "100%" }}>
          <ReactECharts
            option={sankeyOption}
            style={{ height: isMobile ? 820 : 740, width: "100%" }}
            notMerge
          />
        </Box>
      </Box>
      <Typography
        sx={{
          mt: 1,
          fontSize: "1rem",
          color: "#111827",
          textAlign: "center",
        }}
      >
        This visualization shows the flow of document sources from
        geographic regions through institutional categories to specific sources.
      </Typography>
    </>
  );

  if (noCard) {
    return <Box sx={{ mt: 3 }}>{inner}</Box>;
  }

  return (
    <Card sx={{ mx: 0, px: 0, pt: 0 }}>
      <CardContent sx={{ px: 1, py: 2 }}>{inner}</CardContent>
    </Card>
  );
}
